import { useState, useEffect } from 'react';
import { ErrorBoundary } from './components/ErrorBoundary';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import { useCurrentUser } from './hooks/useCurrentUser';
import { canAccess, getDefaultRoute } from './lib/roles';
import Sidebar from './components/Sidebar';
import { Header } from './components/Header';
import { DeskMark } from './components/ui/DeskLogo';
import Login from './pages/Login';
import RedefinirSenha from './pages/RedefinirSenha';
import NovaVenda from './pages/NovaVenda';
import DiarioSDR from './pages/DiarioSDR';
import DiarioSocialSeller from './pages/DiarioSocialSeller';
import DiarioCloser from './pages/DiarioCloser';
import DiarioSalesFarmer from './pages/DiarioSalesFarmer';
import MetasPontuacao from './pages/MetasPontuacao';
import RegistroEventos from './pages/RegistroEventos';
import CadastroLeads from './pages/CadastroLeads';
import CalendarioAgendamentos from './pages/CalendarioAgendamentos';
import Configuracoes from './pages/Configuracoes';
import Comissionamento from './pages/Comissionamento';

// Guard: redireciona se role não tem acesso à rota
function Guard({ role, path, children }) {
  if (!canAccess(role, path)) {
    return <Navigate to={getDefaultRoute(role)} replace />;
  }
  return children;
}

function AuthenticatedShell({ session, theme, onThemeToggle, sidebarOpen, onSidebarToggle }) {
  const { currentUser, role, loadingUser } = useCurrentUser(session);
  const defaultRoute = getDefaultRoute(role);

  if (loadingUser) {
    return (
      <div style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '1.25rem',
      }}>
        <DeskMark size={48} animated={false} glow={false} />
        <div style={{ fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--dim)' }}>
          Carregando
        </div>
      </div>
    );
  }

  // Sidebar flutuante: calcular margem do conteúdo principal
  // Fechada: 18 (gap) + 64 (sidebar) + 18 (gap interno) = 100px
  // Aberta:  18 (gap) + 240 (sidebar) + 18 (gap interno) = 276px
  const contentMargin = sidebarOpen ? '276px' : '100px';

  return (
    <div style={{ position: 'relative', minHeight: '100vh' }}>
      <Sidebar
        session={session}
        theme={theme}
        onThemeToggle={onThemeToggle}
        role={role}
        currentUser={currentUser}
        isOpen={sidebarOpen}
        onToggle={onSidebarToggle}
      />
      <div style={{
        marginLeft: contentMargin,
        transition: 'margin-left 0.32s cubic-bezier(0.16, 1, 0.3, 1)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        paddingTop: '18px',
        paddingRight: '18px',
        paddingBottom: '18px',
      }}>
        <Header currentUser={currentUser} role={role} />
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <main className="main-content">
            <Routes>
              <Route path="/nova-venda" element={
                <Guard role={role} path="/nova-venda">
                  <NovaVenda currentUser={currentUser} role={role} />
                </Guard>
              } />
              <Route path="/diario-sdr" element={
                <Guard role={role} path="/diario-sdr">
                  <DiarioSDR />
                </Guard>
              } />
              <Route path="/diario-social-seller" element={
                <Guard role={role} path="/diario-social-seller">
                  <DiarioSocialSeller />
                </Guard>
              } />
              <Route path="/diario-closer" element={
                <Guard role={role} path="/diario-closer">
                  <DiarioCloser />
                </Guard>
              } />
              <Route path="/diario-sales-farmer" element={
                <Guard role={role} path="/diario-sales-farmer">
                  <DiarioSalesFarmer />
                </Guard>
              } />
              <Route path="/metas-pontuacao" element={
                <Guard role={role} path="/configuracoes">
                  <MetasPontuacao />
                </Guard>
              } />
              <Route path="/registro-eventos" element={<RegistroEventos />} />
              <Route path="/cadastro-leads" element={
                <Guard role={role} path="/cadastro-leads">
                  <CadastroLeads />
                </Guard>
              } />
              <Route path="/calendario" element={<CalendarioAgendamentos currentUser={currentUser} role={role} />} />
              <Route path="/configuracoes" element={
                <Guard role={role} path="/configuracoes">
                  <Configuracoes />
                </Guard>
              } />
              <Route path="/comissionamento" element={
                <Guard role={role} path="/comissionamento">
                  <Comissionamento />
                </Guard>
              } />
              <Route path="*" element={<Navigate to={defaultRoute} replace />} />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [session, setSession] = useState(undefined);
  const [theme, setTheme] = useState(() => localStorage.getItem('desk-theme') || 'dark');
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('desk-sidebar-open');
    return saved === 'true';
  });

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('desk-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    document.documentElement.classList.add('theme-transitioning');
    setTimeout(() => document.documentElement.classList.remove('theme-transitioning'), 250);
    setTheme(t => t === 'light' ? 'dark' : 'light');
  };

  const toggleSidebar = () => {
    setSidebarOpen(o => {
      const next = !o;
      localStorage.setItem('desk-sidebar-open', String(next));
      return next;
    });
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data, error }) => {
      if (error) {
        supabase.auth.signOut().finally(() => setSession(null));
        return;
      }
      setSession(data.session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (event === 'TOKEN_REFRESHED' && !newSession) {
        supabase.auth.signOut().finally(() => setSession(null));
        return;
      }
      setSession(newSession);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (session === undefined) {
    return (
      <div style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '1.25rem',
      }}>
        <DeskMark size={48} animated={false} glow={false} />
        <div style={{
          fontSize: '0.68rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.18em',
          color: 'var(--dim)',
        }}>
          Carregando
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Router>
        <Routes>
          <Route path="/redefinir-senha" element={<RedefinirSenha session={session} />} />
          <Route path="*" element={session
            ? <AuthenticatedShell
                session={session}
                theme={theme}
                onThemeToggle={toggleTheme}
                sidebarOpen={sidebarOpen}
                onSidebarToggle={toggleSidebar}
              />
            : <Login />}
          />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
